/**
 * Shared database utilities for Object-Property-LWW-value system
 * Used by both client (SQLite WASM) and server (Cloudflare Durable Objects)
 * 
 * Includes:
 * - Merkle tree hash computation with race condition protection
 * - Garbage collection for tombstoned objects
 * - Common database operations
 */

import { initializePropertyDrivenDatabase } from '@object-property-lww/database-schema';
import { computeContentHash, computeSubtreeHash, PropertyForHashing } from './hash-utils';

// Export hash utilities for use by client
export { computeContentHash, computeSubtreeHash, PropertyForHashing } from './hash-utils';

// Re-export the basic hash function for internal use
function computeHash(data: string): string {
  let hash = 0;
  if (data.length === 0) return hash.toString(16);
  
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(16).padStart(8, '0');
}


export interface DatabaseAdapter {
  exec(sql: string, params?: any[]): any[];
  prepare?(sql: string): any;
  getRowsModified?(): number;
}

/**
 * Utility class to handle different SQLite result formats consistently
 * Prevents recurring cursor vs array issues between browser sql.js and Cloudflare SQLite
 */
class SQLResultHandler {
  /**
   * Normalize SQL query results to always return an array of objects
   * Handles both browser sql.js and Cloudflare SQLite result formats
   */
  static normalizeResults(result: any): any[] {
    // Already an array - browser sql.js format
    if (Array.isArray(result)) {
      return result;
    }
    
    // Try to iterate as cursor - Cloudflare format
    if (result && typeof result[Symbol.iterator] === 'function') {
      const rows: any[] = [];
      try {
        for (const row of result) {
          if (row) {
            rows.push(row);
          }
        }
        return rows;
      } catch (e) {
        // Fallback: try results property
        if (result.results && Array.isArray(result.results)) {
          return result.results;
        }
      }
    }
    
    // Fallback: check for results property
    if (result?.results && Array.isArray(result.results)) {
      return result.results;
    }
    
    // Empty result
    return [];
  }
}

export class SharedDatabaseUtils {
  constructor(private db: DatabaseAdapter) {}

  /**
   * Check if any objects need hash updates
   */
  hasDirtyHashes(): boolean {
    const result = this.db.exec(`SELECT 1 FROM objects WHERE dirty = 1 LIMIT 1`);
    return result.length > 0;
  }

  /**
   * EXPERIMENTAL: Process dirty objects level by level for optimal performance
   * 1. Process all dirty objects' content hashes first
   * 2. Then propagate subtree hash changes level by level upward
   */
  async processDirtyObjectsLevelByLevel(): Promise<number> {
    let transactionActive = false;
    
    try {
      this.db.exec('BEGIN TRANSACTION');
      transactionActive = true;
      
      // 1. Get all dirty objects sorted by depth (deepest first)
      const dirtyObjectsResult = this.db.exec(`
        WITH RECURSIVE object_depths AS (
          -- Base case: root objects (depth 0)
          SELECT object_id, parent_id, 0 as depth
          FROM objects 
          WHERE parent_id IS NULL
          
          UNION ALL
          
          -- Recursive case: children
          SELECT o.object_id, o.parent_id, od.depth + 1
          FROM objects o
          INNER JOIN object_depths od ON o.parent_id = od.object_id
        )
        SELECT o.object_id, o.updated_at, COALESCE(od.depth, 0) as depth, o.is_tombstoned
        FROM objects o
        LEFT JOIN object_depths od ON o.object_id = od.object_id
        WHERE o.dirty = 1
        ORDER BY od.depth DESC, o.object_id  -- Process deepest objects first
      `);
      const normalizedDirtyObjects = SQLResultHandler.normalizeResults(dirtyObjectsResult);
      
      console.log(`🔄 Processing ${normalizedDirtyObjects.length} dirty objects level by level`);
      
      // 2. PHASE 1: Batch compute and update content hashes for all dirty objects
      const parentsThatNeedSubtreeUpdate = new Set<string>();
      let contentHashUpdates = 0;
      
      // Prepare batch data for content hash updates
      const batchUpdates: Array<{
        objectId: string;
        contentHash: string;
        versionVector: string;
        parentId: string | null;
      }> = [];
      
      for (const row of normalizedDirtyObjects) {
        const objectId = row.object_id;
        const isTombstoned = row.is_tombstoned === 1;
        
        if (!objectId) {
          console.warn('Skipping row with undefined object_id:', row);
          continue;
        }
        
        // Get object info for parent tracking
        const objectResult = this.db.exec(`
          SELECT parent_id, version_vector
          FROM objects 
          WHERE object_id = ?
        `, [objectId]);
        
        if (objectResult.length === 0) {
          console.warn(`Object ${objectId} not found, skipping`);
          continue;
        }
        
        const objectInfo = objectResult[0] as any;
        const parentId = objectInfo.parent_id;
        
        // Query properties for content hash and version vector computation
        let propertiesQuery;
        if (isTombstoned) {
          propertiesQuery = `
            SELECT property, value, replica_id, logical_timestamp
            FROM object_properties 
            WHERE object_id = ? 
              AND property IN ('object.metadata', 'object.tombstoned')
            ORDER BY property
          `;
        } else {
          propertiesQuery = `
            SELECT property, value, replica_id, logical_timestamp
            FROM object_properties 
            WHERE object_id = ?
            ORDER BY property
          `;
        }
        
        const propertiesResult = this.db.exec(propertiesQuery, [objectId]);
        
        // Compute new content hash and version vector
        const contentHash = this.computeContentHashFromProperties(
          propertiesResult.map((row: any) => ({
            property: row.property,
            value: row.value || ''
          }))
        );
        
        const versionVector = this.computeVersionVectorFromProperties(
          propertiesResult.map((row: any) => ({
            replica_id: row.replica_id,
            logical_timestamp: row.logical_timestamp
          }))
        );
        
        // Add to batch
        batchUpdates.push({
          objectId,
          contentHash,
          versionVector: JSON.stringify(versionVector),
          parentId
        });
        
        // Mark parent for subtree hash update (if it exists)
        if (parentId) {
          parentsThatNeedSubtreeUpdate.add(parentId);
        }
      }
      
      // Execute chunked batch updates to avoid SQLITE_TOOBIG errors
      console.log(`🚀 Chunked batch updating ${batchUpdates.length} content hashes...`);
      const currentTime = Date.now();
      const BATCH_CHUNK_SIZE = 100; // Process in chunks of 100 to avoid SQL statement size limits
      
      for (let i = 0; i < batchUpdates.length; i += BATCH_CHUNK_SIZE) {
        const chunk = batchUpdates.slice(i, i + BATCH_CHUNK_SIZE);
        
        if (chunk.length > 0) {
          // Build CASE WHEN statement for this chunk
          const objectIds = chunk.map(u => `'${u.objectId.replace(/'/g, "''")}'`).join(',');
          
          const contentHashCases = chunk
            .map(u => `WHEN '${u.objectId.replace(/'/g, "''")}' THEN '${u.contentHash.replace(/'/g, "''")}'`)
            .join(' ');
          
          const versionVectorCases = chunk
            .map(u => `WHEN '${u.objectId.replace(/'/g, "''")}' THEN '${u.versionVector.replace(/'/g, "''")}'`)
            .join(' ');
          
          const batchUpdateSQL = `
            UPDATE objects 
            SET 
              content_hash = CASE object_id ${contentHashCases} END,
              version_vector = CASE object_id ${versionVectorCases} END,
              dirty = 0,
              updated_at = ${currentTime}
            WHERE object_id IN (${objectIds})
          `;
          
          this.db.exec(batchUpdateSQL);
          contentHashUpdates += chunk.length;
        }
      }
      
      console.log(`✅ Phase 1 complete: Updated ${contentHashUpdates} content hashes`);
      console.log(`🔄 Phase 2: Processing ${parentsThatNeedSubtreeUpdate.size} parents for subtree hash updates`);
      
      // 3. PHASE 2: Batch propagate subtree hash changes level by level
      let subtreeHashUpdates = 0;
      let currentLevelParents = parentsThatNeedSubtreeUpdate;
      let levelNumber = 1;
      
      while (currentLevelParents.size > 0) {
        console.log(`🌳 Level ${levelNumber}: Batch updating ${currentLevelParents.size} subtree hashes`);
        
        const nextLevelParents = new Set<string>();
        const subtreeBatchUpdates: Array<{
          objectId: string;
          subtreeHash: string;
          grandparentId: string | null;
        }> = [];
        
        // Prepare batch data for this level
        for (const parentId of currentLevelParents) {
          // Get parent's current content hash and parent
          const parentResult = this.db.exec(`
            SELECT content_hash, parent_id
            FROM objects 
            WHERE object_id = ?
          `, [parentId]);
          
          if (parentResult.length === 0) {
            console.warn(`Parent ${parentId} not found, skipping`);
            continue;
          }
          
          const parentInfo = parentResult[0] as any;
          const parentContentHash = parentInfo.content_hash;
          const grandparentId = parentInfo.parent_id;
          
          // Compute new subtree hash
          const newSubtreeHash = this.computeSubtreeHash(parentId, parentContentHash);
          
          // Add to batch
          subtreeBatchUpdates.push({
            objectId: parentId,
            subtreeHash: newSubtreeHash,
            grandparentId
          });
          
          // Mark grandparent for next level processing
          if (grandparentId) {
            nextLevelParents.add(grandparentId);
          }
        }
        
        // Execute chunked batch update for subtree hashes at this level
        const levelTime = Date.now();
        const SUBTREE_CHUNK_SIZE = 100; // Process subtree updates in chunks too
        
        for (let j = 0; j < subtreeBatchUpdates.length; j += SUBTREE_CHUNK_SIZE) {
          const chunk = subtreeBatchUpdates.slice(j, j + SUBTREE_CHUNK_SIZE);
          
          if (chunk.length > 0) {
            const levelObjectIds = chunk.map(u => `'${u.objectId.replace(/'/g, "''")}'`).join(',');
            
            const subtreeHashCases = chunk
              .map(u => `WHEN '${u.objectId.replace(/'/g, "''")}' THEN '${u.subtreeHash.replace(/'/g, "''")}'`)
              .join(' ');
            
            const levelBatchSQL = `
              UPDATE objects 
              SET 
                subtree_hash = CASE object_id ${subtreeHashCases} END,
                updated_at = ${levelTime}
              WHERE object_id IN (${levelObjectIds})
            `;
            
            this.db.exec(levelBatchSQL);
            subtreeHashUpdates += chunk.length;
          }
        }
        
        currentLevelParents = nextLevelParents;
        levelNumber++;
        
        if (levelNumber > 20) {
          console.warn(`⚠️  Stopping subtree propagation at level ${levelNumber} to prevent infinite loops`);
          break;
        }
      }
      
      this.db.exec('COMMIT');
      transactionActive = false;
      
      const totalUpdates = contentHashUpdates + subtreeHashUpdates;
      console.log(`🎉 Level-by-level processing complete: ${contentHashUpdates} content + ${subtreeHashUpdates} subtree = ${totalUpdates} total updates`);
      return totalUpdates;
      
    } catch (error) {
      console.error('Level-by-level hash processing failed:', error);
      
      if (transactionActive) {
        try {
          this.db.exec('ROLLBACK');
        } catch (rollbackError) {
          console.warn('Failed to rollback transaction:', rollbackError);
        }
      }
      
      throw error;
    }
  }

  /**
   * Process all dirty objects: update version vectors and recompute hashes using bottom-up propagation
   */
  async processDirtyObjects(): Promise<number> {
    let transactionActive = false;
    
    try {
        this.db.exec('BEGIN TRANSACTION');
        transactionActive = true;
        
        // 1. Get all dirty objects sorted by depth (deepest first for optimal propagation)
        const dirtyObjectsResult = this.db.exec(`
          WITH RECURSIVE object_depths AS (
            -- Base case: root objects (depth 0)
            SELECT object_id, parent_id, 0 as depth
          FROM objects 
            WHERE parent_id IS NULL
            
            UNION ALL
            
            -- Recursive case: children
            SELECT o.object_id, o.parent_id, od.depth + 1
            FROM objects o
            INNER JOIN object_depths od ON o.parent_id = od.object_id
          )
          SELECT o.object_id, o.updated_at, COALESCE(od.depth, 0) as depth
          FROM objects o
          LEFT JOIN object_depths od ON o.object_id = od.object_id
          WHERE o.dirty = 1
          ORDER BY od.depth DESC, o.object_id  -- Process deepest objects first
        `);
        const normalizedDirtyObjects = SQLResultHandler.normalizeResults(dirtyObjectsResult);
        
        let totalUpdatedCount = 0;
        
        // 2. For each dirty object, recompute hash and propagate up to root
        for (const row of normalizedDirtyObjects) {
          const dirtyObjectId = row.object_id;
          
          if (!dirtyObjectId) {
            console.warn('Skipping row with undefined object_id in hash recomputation:', row);
            continue;
          }
          
          // Process this dirty object (version vector + hash propagation)
          const updatedCount = await this.processDirtyObject(dirtyObjectId);
          totalUpdatedCount += updatedCount;
        }
        
        this.db.exec('COMMIT');
        transactionActive = false;
        
        console.log(`Merkle tree recomputation complete: ${totalUpdatedCount} objects updated`);
        return totalUpdatedCount;
        
    } catch (error) {
      console.error('Hash recomputation failed:', error);
      
      // Try to rollback if transaction is active
      if (transactionActive) {
        try {
          this.db.exec('ROLLBACK');
        } catch (rollbackError) {
          console.warn('Failed to rollback transaction:', rollbackError);
        }
      }
      
      throw error;
    }
  }

  /**
   * Compute version vector from a set of properties
   */
  private computeVersionVectorFromProperties(properties: Array<{replica_id: number, logical_timestamp: number}>): Record<string, number> {
    const versionVector: Record<string, number> = {};
    
    for (const prop of properties) {
      const replicaId = prop.replica_id?.toString();
      const timestamp = prop.logical_timestamp;
      
      if (replicaId && timestamp) {
        // Keep the highest timestamp per replica
        const currentTimestamp = versionVector[replicaId] || 0;
        if (timestamp > currentTimestamp) {
          versionVector[replicaId] = timestamp;
        }
      }
    }
    
    return versionVector;
  }

  /**
   * Compute content hash from a set of properties using shared algorithm
   */
  private computeContentHashFromProperties(properties: Array<{property: string, value: string}>): string {
    return computeContentHash(properties);
  }

  /**
   * Compute subtree hash from content hash and children's subtree hashes
   */
  private computeSubtreeHash(objectId: string, contentHash: string): string {
    const childrenResult = this.db.exec(`
      SELECT object_id, subtree_hash
      FROM objects 
      WHERE parent_id = ?
      ORDER BY object_id
    `, [objectId]);
    
    const childSubtreeHashes = childrenResult.map((row: any) => row.subtree_hash || '');
    return computeSubtreeHash(contentHash, childSubtreeHashes);
  }

  /**
   * Process a dirty object: update its version vector and propagate hash changes up to the root
   * Following proper Merkle tree bottom-up recomputation
   */
  private async processDirtyObject(startObjectId: string): Promise<number> {
    let currentObjectId: string | null = startObjectId;
      let updatedCount = 0;
    const visitedObjects = new Set<string>(); // Track visited objects to detect circular references
    
    while (currentObjectId) {
      // Check for circular reference
      if (visitedObjects.has(currentObjectId)) {
        throw new Error(`Circular reference detected in object hierarchy: ${startObjectId} -> ... -> ${currentObjectId}`);
      }
      visitedObjects.add(currentObjectId);
      
      // Get current object info (include tombstone status and version_vector)
      const objectResult = this.db.exec(`
        SELECT parent_id, updated_at, version_vector, is_tombstoned
        FROM objects 
        WHERE object_id = ?
      `, [currentObjectId]);
      
      if (objectResult.length === 0) {
        console.warn(`Object ${currentObjectId} not found, stopping propagation`);
        break;
      }
      
      const objectInfo = objectResult[0] as any;
      const parentId = objectInfo.parent_id;
      const snapshotUpdatedAt = objectInfo.updated_at;
      const existingVersionVector = objectInfo.version_vector;
      const isTombstoned = objectInfo.is_tombstoned === 1;
      
      try {
        // 2. Only query properties for the actual dirty object
        const isDirtyObject = currentObjectId === startObjectId;
        let propertiesResult: any[] = [];
        
        if (isDirtyObject) {
          // Query properties for dirty object (filter by tombstone status)
        let propertiesQuery;
        if (isTombstoned) {
          // For tombstoned objects, only include essential properties that survive garbage collection
          propertiesQuery = `
            SELECT property, value, replica_id, logical_timestamp
            FROM object_properties 
            WHERE object_id = ? 
              AND property IN ('object.metadata', 'object.tombstoned')
            ORDER BY property
          `;
        } else {
          // For live objects, include all properties
          propertiesQuery = `
            SELECT property, value, replica_id, logical_timestamp
            FROM object_properties 
            WHERE object_id = ?
            ORDER BY property
          `;
          }
          propertiesResult = this.db.exec(propertiesQuery, [currentObjectId]);
        }
        
        // 3. Optimize: only recompute content hash for dirty object, reuse for ancestors
        let contentHash: string;
        let versionVector: Record<string, number>;
        
        if (isDirtyObject) {
          // For the dirty object: recompute both content hash and version vector
          contentHash = this.computeContentHashFromProperties(
          propertiesResult.map((row: any) => ({
            property: row.property,
            value: row.value || ''
          }))
        );
          versionVector = this.computeVersionVectorFromProperties(
              propertiesResult.map((row: any) => ({
                replica_id: row.replica_id,
                logical_timestamp: row.logical_timestamp
              }))
          );
        } else {
          // For ancestors: get existing content hash, preserve version vector
          const existingHashResult = this.db.exec(`
            SELECT content_hash FROM objects WHERE object_id = ?
          `, [currentObjectId]);
          contentHash = existingHashResult[0]?.content_hash || '';
          versionVector = existingVersionVector ? JSON.parse(existingVersionVector) : {};
        }
        
        // Always recompute subtree hash (depends on children)
        const subtreeHash = this.computeSubtreeHash(currentObjectId, contentHash);
        
        
        
        // 6. Update object with appropriate fields
        if (isDirtyObject) {
          // This is the originally dirty object - update version vector too
          this.db.exec(`
            UPDATE objects 
            SET 
              content_hash = ?, 
              subtree_hash = ?, 
              version_vector = ?,
              dirty = 0,
              updated_at = ?
            WHERE object_id = ? 
              AND updated_at = ?
          `, [contentHash, subtreeHash, JSON.stringify(versionVector), Date.now(), currentObjectId, snapshotUpdatedAt]);
        } else {
          // This is an ancestor - only update hash, preserve version vector
          this.db.exec(`
            UPDATE objects 
            SET 
              content_hash = ?, 
              subtree_hash = ?, 
              updated_at = ?
            WHERE object_id = ? 
              AND updated_at = ?
          `, [contentHash, subtreeHash, Date.now(), currentObjectId, snapshotUpdatedAt]);
        }
        
        // Assume update succeeded (skip expensive rowsModified check for now)
          updatedCount++;
        
        // 5. Move up to parent for next iteration
        currentObjectId = parentId;
        
        
      } catch (objectError) {
        console.error(`Failed to compute hash for object ${currentObjectId}:`, objectError);
        break; // Stop propagation on error
      }
    }
    
    return updatedCount;
  }

  /**
   * Get document root hash (computed from all root objects)
   */
  async getDocumentRootHash(): Promise<{combined_root_hash: string, computed_at: number, root_object_count: number}> {
    // Ensure hashes are up-to-date first
    if (this.hasDirtyHashes()) {
      await this.processDirtyObjects();
    }
    
    const result = this.db.exec(`SELECT * FROM document_root_hashes`);
    
    if (result.length === 0 || (result[0] as any).root_object_count === 0) {
      return {
        combined_root_hash: 'empty',
        computed_at: Date.now(),
        root_object_count: 0
      };
    }
    
    const data = result[0] as any;
    
    // Compute hash in TypeScript (cross-platform compatible)
    const combined_root_hash = data.root_hashes_data ? 
      computeHash(data.root_hashes_data) : 'empty';
    
    return {
      combined_root_hash,
      computed_at: data.computed_at || Date.now(),
      root_object_count: data.root_object_count || 0
    };
  }


  /**
   * Fetch properties for specific objects (used for selective sync)
   * Returns all properties per object atomically to avoid race conditions
   */
  async fetchPropertiesForObjects(objectIds: string[]): Promise<Array<{
    object_id: string;
    object_type: string;
    is_tombstoned: boolean;
    version_vector: Record<string, number>;
    properties: Array<{
      property: string;
      value: string;
      lww_counter: number;
      lww_random: number;
      logical_timestamp: number;
      replica_id: number;
      user_id: string;
      updated_at: number;
    }>;
  }>> {
    if (objectIds.length === 0) {
      return [];
    }

    // Ensure hashes are up-to-date first
    if (this.hasDirtyHashes()) {
      await this.processDirtyObjects();
    }

    const results: Array<any> = [];
    
    for (const objectId of objectIds) {
      // Get object metadata
      const objectResult = this.db.exec(`
        SELECT object_type, is_tombstoned, version_vector
        FROM objects 
        WHERE object_id = ?
      `, [objectId]);
      
      if (objectResult.length === 0) {
        console.warn(`Object ${objectId} not found for property fetch`);
        continue;
      }
      
      const objectInfo = objectResult[0] as any;
      const isTombstoned = objectInfo.is_tombstoned === 1;
      
      // Get properties (GC-aware filtering)
      let propertiesQuery;
      if (isTombstoned) {
        // For tombstoned objects, only return essential properties that survive garbage collection
        propertiesQuery = `
          SELECT property, value, lww_counter, lww_random, logical_timestamp, replica_id, user_id, updated_at
          FROM object_properties 
          WHERE object_id = ? 
            AND property IN ('object.metadata', 'object.tombstoned')
          ORDER BY property
        `;
      } else {
        // For live objects, return all properties
        propertiesQuery = `
          SELECT property, value, lww_counter, lww_random, logical_timestamp, replica_id, user_id, updated_at
          FROM object_properties 
          WHERE object_id = ?
          ORDER BY property
        `;
      }
      
      const propertiesResult = this.db.exec(propertiesQuery, [objectId]);
      
      // Parse version vector
      let versionVector: Record<string, number> = {};
      if (objectInfo.version_vector) {
        try {
          versionVector = JSON.parse(objectInfo.version_vector);
        } catch (e) {
          console.warn(`Invalid version vector for object ${objectId}:`, objectInfo.version_vector);
        }
      }
      
      results.push({
        object_id: objectId,
        object_type: objectInfo.object_type,
        is_tombstoned: isTombstoned,
        version_vector: versionVector,
        properties: propertiesResult.map((row: any) => ({
          property: row.property,
          value: row.value,
          lww_counter: row.lww_counter,
          lww_random: row.lww_random,
          logical_timestamp: row.logical_timestamp,
          replica_id: row.replica_id,
          user_id: row.user_id,
          updated_at: row.updated_at
        }))
      });
      
      console.log(`📦 Fetched ${propertiesResult.length} properties for ${isTombstoned ? 'tombstoned' : 'live'} object ${objectId}`);
    }
    
    return results;
  }

  /**
   * Fetch complete subtrees starting from specific root objects
   * Returns all descendants of the specified objects using recursive traversal
   */
  async fetchSubtrees(rootObjectIds: string[], maxDepth: number = 10): Promise<Array<{
    root_object_id: string;
    objects: Array<{
      object_id: string;
      parent_id: string | null;
      object_type: string;
      is_tombstoned: boolean;
      version_vector: Record<string, number>;
      depth: number;
      properties: Array<{
        property: string;
        value: string;
        lww_counter: number;
        lww_random: number;
        logical_timestamp: number;
        replica_id: number;
        user_id: string;
        updated_at: number;
      }>;
    }>;
  }>> {
    if (rootObjectIds.length === 0) {
      return [];
    }

    // Ensure hashes are up-to-date first
    if (this.hasDirtyHashes()) {
      await this.processDirtyObjects();
    }

    const results: Array<any> = [];

    for (const rootObjectId of rootObjectIds) {
      // Use iterative approach for cross-platform compatibility (sql.js + better-sqlite3)
      const subtreeResult = this.fetchSubtreesIterative(rootObjectId, maxDepth);
      
      if (subtreeResult.length === 0) {
        console.warn(`No objects found for subtree ${rootObjectId}`);
        continue;
      }

      // Fetch properties for all objects in the subtree
      const objectIds = subtreeResult.map((row: any) => row.object_id);
      const objectsWithProperties = await this.fetchPropertiesForObjects(objectIds);
      
      // Create a map for quick lookup
      const propertiesMap = new Map(
        objectsWithProperties.map(obj => [obj.object_id, obj])
      );

      // Build the subtree structure
      const subtreeObjects = subtreeResult.map((row: any) => {
        const objectData = propertiesMap.get(row.object_id);
        
        // Parse version vector
        let versionVector: Record<string, number> = {};
        if (row.version_vector) {
          try {
            versionVector = JSON.parse(row.version_vector);
          } catch (e) {
            console.warn(`Invalid version vector for object ${row.object_id}:`, row.version_vector);
          }
        }

        return {
          object_id: row.object_id,
          parent_id: row.parent_id,
          object_type: row.object_type,
          is_tombstoned: row.is_tombstoned === 1,
          version_vector: versionVector,
          depth: row.depth,
          properties: objectData ? objectData.properties : []
        };
      });

      results.push({
        root_object_id: rootObjectId,
        objects: subtreeObjects
      });

      console.log(`🌳 Fetched subtree for ${rootObjectId}: ${subtreeObjects.length} objects (max depth: ${Math.max(...subtreeObjects.map(o => o.depth))})`);
    }

    return results;
  }

  /**
   * Iterative fallback for fetchSubtrees to work around sql.js recursive CTE limitations
   * Uses level-by-level traversal instead of recursive CTE
   */
  private fetchSubtreesIterative(rootObjectId: string, maxDepth: number): any[] {
    const results: any[] = [];
    const processedIds = new Set<string>();
    let currentLevel = [rootObjectId];
    let depth = 0;

    while (currentLevel.length > 0 && depth <= maxDepth) {
      const levelIds = currentLevel.filter(id => !processedIds.has(id));
      if (levelIds.length === 0) break;

      // Get objects at current level (chunked to avoid SQL variable limits)
      const FETCH_CHUNK_SIZE = 100; // Process in chunks to avoid "too many SQL variables" error
      
      for (let i = 0; i < levelIds.length; i += FETCH_CHUNK_SIZE) {
        const chunk = levelIds.slice(i, i + FETCH_CHUNK_SIZE);
        const placeholders = chunk.map(() => '?').join(',');
        const levelQuery = `
          SELECT 
            object_id, 
            parent_id, 
            ${depth} as depth,
            object_type,
            is_tombstoned,
            version_vector
          FROM objects 
          WHERE object_id IN (${placeholders})
          ORDER BY object_id
        `;

        const levelResult = this.db.exec(levelQuery, chunk);
        
        // Add to results and mark as processed
        for (const row of levelResult) {
          results.push(row);
          processedIds.add(row.object_id);
        }
      }

      // Find children for next level (also chunked)
      if (depth < maxDepth) {
        const allChildren: any[] = [];
        
        for (let i = 0; i < levelIds.length; i += FETCH_CHUNK_SIZE) {
          const chunk = levelIds.slice(i, i + FETCH_CHUNK_SIZE);
          const childPlaceholders = chunk.map(() => '?').join(',');
          const childQuery = `
            SELECT DISTINCT object_id
            FROM objects 
            WHERE parent_id IN (${childPlaceholders})
            ORDER BY object_id
          `;
          const childResult = this.db.exec(childQuery, chunk);
          allChildren.push(...childResult);
        }
        
        currentLevel = allChildren.map((row: any) => row.object_id);
      } else {
        currentLevel = [];
      }

      depth++;
    }

    return results;
  }

  /**
   * Get version vectors for all objects
   */
  getAllVersionVectors(): Record<string, Record<string, number>> {
    const result = this.db.exec(`
      SELECT object_id, version_vector 
      FROM objects 
      WHERE version_vector IS NOT NULL
    `);
    
    const vectors: Record<string, Record<string, number>> = {};
    
    for (const row of result) {
      try {
        vectors[(row as any).object_id] = JSON.parse((row as any).version_vector || '{}');
      } catch (e) {
        console.warn('Invalid version vector for object:', (row as any).object_id);
        vectors[(row as any).object_id] = {};
      }
    }
    
    return vectors;
  }

  /**
   * Compare version vectors to determine sync needs
   */
  compareVersionVectors(local: Record<string, number>, remote: Record<string, number>): {
    needsRemote: boolean;
    needsLocal: boolean;
    isConflict: boolean;
  } {
    const allReplicas = new Set([...Object.keys(local), ...Object.keys(remote)]);
    
    let localNewer = false;
    let remoteNewer = false;
    
    for (const replica of allReplicas) {
      const localTs = local[replica] || 0;
      const remoteTs = remote[replica] || 0;
      
      if (localTs > remoteTs) {
        localNewer = true;
      } else if (remoteTs > localTs) {
        remoteNewer = true;
      }
    }
    
    return {
      needsRemote: remoteNewer,
      needsLocal: localNewer,
      isConflict: localNewer && remoteNewer
    };
  }

  /**
   * Get object hashes with controllable depth for partial sync
   */
  async getHashesWithDepth(targetDepth: number = 0, includeVersionVectors: boolean = false, documentId?: string): Promise<Array<{
    object_id: string;
    parent_id: string | null;
    content_hash: string | null;
    subtree_hash: string | null;
    depth: number;
    object_type: string;
    version_vector: Record<string, number> | null;
  }>> {
    // Ensure hashes are up-to-date first
    if (this.hasDirtyHashes()) {
      await this.processDirtyObjects();
    }
    
    // Recursive CTE to get objects at the specified depth only
    const versionVectorSelect = includeVersionVectors ? ', o.version_vector' : ', NULL as version_vector';
    
    // If documentId is provided, start from that specific document, otherwise find all root objects
    const baseCondition = documentId ? 'object_id = ?' : 'parent_id IS NULL';
    
    const query = `
      WITH RECURSIVE object_hierarchy(object_id, parent_id, depth) AS (
        -- Base case: start from specified document or all root objects (depth 0)
        SELECT object_id, parent_id, 0 as depth
        FROM objects 
        WHERE ${baseCondition}
        
        UNION ALL
        
        -- Recursive case: children up to target depth
        SELECT o.object_id, o.parent_id, oh.depth + 1
        FROM objects o
        INNER JOIN object_hierarchy oh ON o.parent_id = oh.object_id
        WHERE oh.depth < ?
      )
      SELECT 
        o.object_id,
        o.parent_id,
        o.content_hash,
        o.subtree_hash,
        oh.depth,
        o.object_type
        ${versionVectorSelect}
      FROM object_hierarchy oh
      INNER JOIN objects o ON oh.object_id = o.object_id
      WHERE oh.depth = ?  -- Only return objects AT the target depth
      ORDER BY o.object_id
    `;
    
    const params = documentId 
      ? [documentId, targetDepth + 1, targetDepth]
      : [targetDepth + 1, targetDepth];
    
    const result = this.db.exec(query, params);
    
    return result.map((row: any) => ({
      object_id: row.object_id,
      parent_id: row.parent_id,
      content_hash: row.content_hash,
      subtree_hash: row.subtree_hash,
      depth: row.depth,
      object_type: row.object_type,
      version_vector: row.version_vector ? JSON.parse(row.version_vector) : null
    }));
  }

  /**
   * Compare local and remote hashes to determine which objects need syncing
   */
  compareHashesForSync(
    localHashes: Array<{object_id: string, content_hash: string | null, subtree_hash: string | null}>,
    remoteHashes: Array<{object_id: string, content_hash: string | null, subtree_hash: string | null}>
  ): {
    needsFromRemote: string[];
    needsToRemote: string[];
    conflicts: string[];
  } {
    const localMap = new Map(localHashes.map(h => [h.object_id, h]));
    const remoteMap = new Map(remoteHashes.map(h => [h.object_id, h]));
    
    const needsFromRemote: string[] = [];
    const needsToRemote: string[] = [];
    const conflicts: string[] = [];
    
    // Check what we need from remote
    for (const [objectId, remoteHash] of remoteMap) {
      const localHash = localMap.get(objectId);
      
      if (!localHash) {
        // Object doesn't exist locally
        needsFromRemote.push(objectId);
      } else if (localHash.subtree_hash !== remoteHash.subtree_hash) {
        // Hash mismatch - could be conflict or just different
        needsFromRemote.push(objectId);
      }
    }
    
    // Check what remote needs from us
    for (const [objectId, localHash] of localMap) {
      const remoteHash = remoteMap.get(objectId);
      
      if (!remoteHash) {
        // Object doesn't exist on remote
        needsToRemote.push(objectId);
      } else if (localHash.subtree_hash !== remoteHash.subtree_hash) {
        // Hash mismatch
        if (needsFromRemote.includes(objectId)) {
          // Both sides have changes - potential conflict
          conflicts.push(objectId);
        } else {
          needsToRemote.push(objectId);
        }
      }
    }
    
    return { needsFromRemote, needsToRemote, conflicts };
  }

  /**
   * Get root hash for sync (ensures hashes are up-to-date first)
   */
  async getRootHashForSync(documentId: string): Promise<string> {
    // Ensure all hashes are current
    if (this.hasDirtyHashes()) {
      await this.processDirtyObjects();
    }
    
    const result = this.db.exec(`
      SELECT subtree_hash FROM objects 
      WHERE object_id = ? AND parent_id IS NULL
    `, [documentId]);
    
    return result[0]?.subtree_hash || '';
  }

  /**
   * Get hashes for all children of a given object
   */
  async getChildrenHashes(parentId: string): Promise<Record<string, string>> {
    // Ensure hashes are up-to-date
    if (this.hasDirtyHashes()) {
      await this.processDirtyObjects();
    }
    
    const children = this.db.exec(`
      SELECT object_id, subtree_hash
      FROM objects 
      WHERE parent_id = ?
      ORDER BY object_id
    `, [parentId]);
    
    const hashes: Record<string, string> = {};
    for (const child of children) {
      const c = child as any;
      hashes[c.object_id] = c.subtree_hash || '';
    }
    
    return hashes;
  }

  /**
   * Garbage collect properties of tombstoned objects
   */
  async garbageCollect(): Promise<{objectsProcessed: number, propertiesDeleted: number}> {
    console.log('Starting garbage collection of tombstoned object properties...');
    
    // Get all tombstoned objects
    const tombstonedObjectsResult = this.db.exec(`SELECT object_id FROM objects WHERE is_tombstoned = 1`);
    
    // Normalize results using SQLResultHandler to handle cursor vs array differences
    const normalizedResults = SQLResultHandler.normalizeResults(tombstonedObjectsResult);
    const tombstonedObjects = normalizedResults
      .map((row: any) => ({ object_id: row.object_id }))
      .filter(obj => obj.object_id);
    
    console.log(`Found ${tombstonedObjects.length} tombstoned objects for garbage collection`);
    
    if (tombstonedObjects.length === 0) {
      return {objectsProcessed: 0, propertiesDeleted: 0};
    }
    
    let totalPropertiesDeleted = 0;
    
    // Use prepared statement if available, otherwise use individual queries
    if (this.db.prepare) {
      const deletePropsStmt = this.db.prepare(`
        DELETE FROM object_properties 
        WHERE object_id = ? 
          AND property NOT GLOB 'object.tombstoned' 
          AND property NOT GLOB 'object.metadata'
      `);
      
      for (const obj of tombstonedObjects) {
        const objectId = obj.object_id;
        if (!objectId) {
          console.warn('Skipping row with undefined object_id:', obj);
          continue;
        }
        
        // Handle different prepared statement APIs
        if (deletePropsStmt.run) {
          // Node.js style (better-sqlite3)
        deletePropsStmt.run([objectId]);
        } else if (deletePropsStmt.bind && deletePropsStmt.step) {
          // sql.js style
          deletePropsStmt.bind([objectId]);
          deletePropsStmt.step();
          deletePropsStmt.reset();
        }
        
        const rowsModified = this.db.getRowsModified ? this.db.getRowsModified() : 0;
        totalPropertiesDeleted += rowsModified;
        
        if (rowsModified > 0) {
          console.log(`Garbage collected ${rowsModified} properties from object ${objectId}`);
        }
      }
      
      // Clean up prepared statement for sql.js
      if (deletePropsStmt.free) {
        deletePropsStmt.free();
      }
    } else {
      // Fallback for databases without prepared statements
      for (const obj of tombstonedObjects) {
        const objectId = obj.object_id;
        if (!objectId) {
          console.warn('Skipping row with undefined object_id:', obj);
          continue;
        }
        
        this.db.exec(`
          DELETE FROM object_properties 
          WHERE object_id = ? 
            AND property NOT GLOB 'object.tombstoned' 
            AND property NOT GLOB 'object.metadata'
        `, [objectId]);
        
        const rowsModified = this.db.getRowsModified ? this.db.getRowsModified() : 0;
        totalPropertiesDeleted += rowsModified;
        
        if (rowsModified > 0) {
          console.log(`Garbage collected ${rowsModified} properties from object ${objectId}`);
        }
      }
    }
    
    console.log(`Garbage collection complete: ${totalPropertiesDeleted} properties deleted from ${tombstonedObjects.length} objects`);
    
    return {
      objectsProcessed: tombstonedObjects.length,
      propertiesDeleted: totalPropertiesDeleted
    };
  }

  /**
   * Import properties with LWW conflict resolution while preserving original metadata
   * Used for sync operations where we want to preserve server/peer timestamps
   */
  async importProperties(properties: Array<{
    object_id: string;
    property: string;
    value: string;
    lww_counter: number;
    lww_random: number;
    replica_id: number;
    logical_timestamp: number;
    updated_at: number;
    user_id: string;
  }>): Promise<{imported: number, conflicts: number, errors: string[]}> {
    // Import properties with LWW conflict resolution
    
    // Sort properties to ensure object.metadata comes first for each object, then by logical timestamp
    const sortedProperties = [...properties].sort((a, b) => {
      // First sort by object_id to group properties by object
      if (a.object_id !== b.object_id) {
        return a.object_id.localeCompare(b.object_id);
      }
      
      // Within the same object, ensure object.metadata comes first
      if (a.property === 'object.metadata' && b.property !== 'object.metadata') {
        return -1;
      }
      if (b.property === 'object.metadata' && a.property !== 'object.metadata') {
        return 1;
      }
      
      // For other properties, sort by logical timestamp for consistent processing
      return a.logical_timestamp - b.logical_timestamp;
    });
    
    // Properties sorted by object_id > metadata_first > logical_timestamp for consistent LWW processing
    
    let imported = 0;
    let conflicts = 0;
    const errors: string[] = [];
    
    try {
      this.db.exec('BEGIN TRANSACTION');
      
      for (const property of sortedProperties) {
        try {
          const { object_id, property: propName, value, lww_counter, lww_random, replica_id, logical_timestamp, updated_at, user_id } = property;
          
          // Validate required fields
          if (!object_id || !propName || lww_counter === undefined || lww_random === undefined) {
            errors.push(`Invalid property data: missing required fields for ${object_id}:${propName}`);
            continue;
          }
          
          const isSpecialProperty = propName.startsWith('object.');
          
          // For non-special properties, check tombstone ancestry
          if (!isSpecialProperty) {
            const ancestryCheck = this.db.exec(`
              SELECT has_tombstoned_ancestor 
              FROM tombstone_ancestry 
              WHERE object_id = ?
            `, [object_id]);
            
            if (ancestryCheck.length > 0 && ancestryCheck[0].has_tombstoned_ancestor) {
              console.log(`Skipping property ${propName} for tombstoned object ${object_id}`);
              continue;
            }
          }
          
          if (isSpecialProperty) {
            // Special properties: no LWW conflict resolution, INSERT OR IGNORE
            const existingCheck = this.db.exec(`
              SELECT 1 FROM object_properties 
              WHERE object_id = ? AND property = ?
            `, [object_id, propName]);
            
            if (existingCheck.length === 0) {
              // Convert boolean values to strings for database storage and trigger compatibility
              const stringValue = typeof value === 'boolean' ? String(value) : value;
              
              this.db.exec(`
                INSERT INTO object_properties (
                  object_id, property, value, replica_id, logical_timestamp, updated_at, user_id
                ) VALUES (?, ?, ?, ?, ?, ?, ?)
              `, [object_id, propName, stringValue, replica_id, logical_timestamp, updated_at, user_id]);
              imported++;
              
              // Note: Object automatically marked dirty by database trigger
            } else {
              // Skip existing metadata - no logging needed
            }
          } else {
            // Regular properties: full LWW conflict resolution with preserved metadata
            const existingResult = this.db.exec(`
              SELECT lww_counter, lww_random, value, updated_at
              FROM object_properties 
              WHERE object_id = ? AND property = ?
            `, [object_id, propName]);
            
            let shouldUpdate = true;
            
            if (existingResult.length > 0) {
              const existing = existingResult[0] as any;
              const existingCounter = existing.lww_counter;
              const existingRandom = existing.lww_random;
              
              // LWW conflict resolution
              if (lww_counter < existingCounter) {
                shouldUpdate = false;
              } else if (lww_counter === existingCounter) {
                if (lww_random < existingRandom) {
                  shouldUpdate = false;
                } else if (lww_random === existingRandom && value === existing.value) {
                  shouldUpdate = false; // Same value, no update needed
                }
              }
              
              if (!shouldUpdate) {
                conflicts++;
                // LWW conflict resolved - no individual logging needed
              }
            }
            
            if (shouldUpdate) {
              // Convert boolean values to strings for database storage and trigger compatibility
              const stringValue = typeof value === 'boolean' ? String(value) : value;
              
              this.db.exec(`
                INSERT OR REPLACE INTO object_properties (
                  object_id, property, value, lww_counter, lww_random,
                  replica_id, logical_timestamp, updated_at, user_id
                ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
              `, [object_id, propName, stringValue, lww_counter, lww_random, replica_id, logical_timestamp, updated_at, user_id]);
              
              imported++;
              
              // Note: Object automatically marked dirty by database trigger
            }
          }
        } catch (propError) {
          errors.push(`Failed to import property ${property.object_id}:${property.property}: ${propError}`);
        }
      }
      
      // After importing all properties, ensure objects table is consistent
      // This is especially important when importing object.metadata properties
      const metadataImported = properties.some(p => p.property === 'object.metadata');
      if (metadataImported) {
        // Rebuilding objects table due to metadata imports
        await this.rebuildObjectsTable();
      }
      
      // Note: Objects are automatically marked dirty by database triggers when properties are inserted
      
      this.db.exec('COMMIT');
      
      const uniqueObjectIds = [...new Set(properties.map(p => p.object_id))];
      if (imported > 0 || conflicts > 0 || errors.length > 0) {
        console.log(`📥 Import: ${imported} new, ${conflicts} conflicts, ${uniqueObjectIds.length} objects`);
      }
      
      return { imported, conflicts, errors };
      
    } catch (error) {
      try {
        this.db.exec('ROLLBACK');
      } catch (rollbackError) {
        console.warn('Failed to rollback import transaction:', rollbackError);
      }
      
      console.error('Property import failed:', error);
      throw error;
    }
  }

  /**
   * Rebuild objects table from properties (consistency check)
   */
  async rebuildObjectsTable(): Promise<{objectsCreated: number, errors: string[]}> {
    // Rebuilding objects table from properties...
    
    const errors: string[] = [];
    let objectsCreated = 0;
    
    try {
      this.db.exec('DELETE FROM objects');
      
      const metadataResult = this.db.exec(`
        SELECT object_id, value, replica_id, updated_at, user_id
        FROM object_properties 
        WHERE property = 'object.metadata'
        ORDER BY updated_at
      `);
      
      for (const row of metadataResult) {
        const r = row as any;
        
        if (!r.object_id) {
          errors.push(`Row missing object_id: ${JSON.stringify(r)}`);
          continue;
        }
        
        try {
          const metadata = JSON.parse(r.value);
          const parentId = metadata.parent_id || null;
          const objectType = metadata.type;
          
          if (!objectType) {
            errors.push(`Object ${r.object_id}: missing type in metadata`);
            continue;
          }
          
          // Check if ancestor is tombstoned
          let isTombstoned = 0;
          if (parentId) {
            const ancestryCheck = this.db.exec(`
              WITH RECURSIVE ancestry(object_id, current_object_id, is_tombstoned) AS (
                SELECT o.object_id, o.object_id, o.is_tombstoned
                FROM objects o WHERE o.object_id = ?
                UNION ALL
                SELECT a.object_id, parent.object_id, parent.is_tombstoned
                FROM ancestry a
                JOIN objects current ON current.object_id = a.current_object_id
                JOIN objects parent ON parent.object_id = current.parent_id
                WHERE current.parent_id IS NOT NULL
              )
              SELECT MAX(is_tombstoned) as has_tombstoned_ancestor
              FROM ancestry
            `, [parentId]);
            
            if (ancestryCheck[0]?.has_tombstoned_ancestor) {
              isTombstoned = 1;
            }
          }
          
          // Check if this object has a tombstone property
          const tombstoneCheck = this.db.exec(`
            SELECT 1 FROM object_properties 
            WHERE object_id = ? AND property = 'object.tombstoned' AND value = 'true'
          `, [r.object_id]);
          
          if (tombstoneCheck.length > 0) {
            isTombstoned = 1;
          }
          
          // Insert object
          this.db.exec(`
            INSERT OR REPLACE INTO objects (
              object_id, parent_id, object_type, is_tombstoned,
              created_at, created_by_replica, created_by_user, 
              dirty, updated_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, 1, ?)
          `, [
            r.object_id, parentId, objectType, isTombstoned,
            r.updated_at, r.replica_id, r.user_id, Date.now()
          ]);
          
          objectsCreated++;
          
        } catch (error) {
          const errorMsg = `Object ${r.object_id}: ${error}`;
          console.error(errorMsg);
          errors.push(errorMsg);
        }
      }
      
      // Objects table rebuilt silently unless errors occur
      
      if (errors.length > 0) {
        console.warn('Rebuild errors:', errors);
      }
      
      return {objectsCreated, errors};
      
    } catch (error) {
      console.error('Failed to rebuild objects table:', error);
      throw error;
    }
  }
}
